<div class="sui-date-time-picker">
  @if (mode === "datetime") {
    <!-- Calendar + time. One [ngpDatePicker] root; the panel body is declared
         inside it so the popover-portaled copy still resolves the primitive's
         state at this declaration site. -->
    <div
      class="sui-date-time-picker__root"
      [class.sui-date-time-picker__root--inline]="inline"
      ngpDatePicker
      [ngpDatePickerDate]="primitiveDate()"
      [ngpDatePickerMin]="minDate"
      [ngpDatePickerMax]="maxDate"
      [ngpDatePickerDisabled]="isDisabled()"
      [ngpDatePickerDateDisabled]="dateDisabled"
      [ngpDatePickerFirstDayOfWeek]="firstDayOfWeek"
      (ngpDatePickerDateChange)="onDateChange($event)"
      (ngpDatePickerFocusedDateChange)="onFocusedDateChange($event)"
    >
      @if (inline) {
        <ng-container
          *ngTemplateOutlet="
            panelBody;
            context: { $implicit: popoverContainer }
          "
        />
      } @else {
        <div class="sui-date-time-picker__field" #fieldEl>
          <sui-input
            class="sui-date-time-picker__field-input"
            [id]="effectiveId"
            [placeholder]="placeholder"
            [disabled]="isDisabled()"
            [ngModel]="inputText()"
            [ngModelOptions]="{ standalone: true }"
            [invalid]="inputInvalid()"
            (ngModelChange)="onInputText($event)"
            (keydown.enter)="onInputEnter()"
            (focusin)="onInputFocus()"
            (focusout)="onInputBlur()"
          >
            <button
              sui-suffix
              type="button"
              class="sui-date-time-picker__trigger"
              aria-label="Open date and time picker"
              [suiPopoverTrigger]="panelTpl"
              [suiPopoverAnchor]="fieldEl"
              [suiPopoverContainer]="resolvedPopoverContainer"
              [suiPopoverTriggerDisabled]="isDisabled()"
            >
              <fa-icon [icon]="triggerIcon()" />
            </button>
          </sui-input>
        </div>

        <ng-template #panelTpl>
          <div suiPopover class="sui-date-time-picker__panel" #panelEl>
            <ng-container
              *ngTemplateOutlet="panelBody; context: { $implicit: panelEl }"
            />
          </div>
        </ng-template>
      }

      <ng-template #panelBody let-selectContainer>
        <div class="sui-date-time-picker__header">
          <button
            type="button"
            class="sui-date-time-picker__nav"
            ngpDatePickerPreviousMonth
            aria-label="Previous month"
          >
            <fa-icon [icon]="faPrev" />
          </button>

          <div class="sui-date-time-picker__nav-selects">
            <sui-select
              class="sui-date-time-picker__month-select"
              aria-label="Month"
              [disabled]="isDisabled()"
              [dropdownContainer]="selectContainer"
              [ngModel]="focusedMonth()"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onMonthChange($event)"
            >
              @for (m of monthOptions(); track m.value) {
                <sui-select-option
                  [value]="m.value"
                  [label]="m.label"
                  [disabled]="m.disabled"
                  >{{ m.label }}</sui-select-option
                >
              }
            </sui-select>

            <sui-select
              class="sui-date-time-picker__year-select"
              aria-label="Year"
              [disabled]="isDisabled()"
              [dropdownContainer]="selectContainer"
              [ngModel]="focusedYear()"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onYearChange($event)"
            >
              @for (y of years(); track y) {
                <sui-select-option [value]="y" [label]="y.toString()">{{
                  y
                }}</sui-select-option>
              }
            </sui-select>
          </div>

          <span class="sui-date-time-picker__sr-label" ngpDatePickerLabel>{{
            label()
          }}</span>

          <button
            type="button"
            class="sui-date-time-picker__nav"
            ngpDatePickerNextMonth
            aria-label="Next month"
          >
            <fa-icon [icon]="faNext" />
          </button>
        </div>

        <sui-calendar
          [showWeekNumbers]="showWeekNumbers"
          [ariaLabel]="label()"
        />

        <div class="sui-date-time-picker__time">
          <sui-time-picker
            [seconds]="seconds"
            [meridian]="meridian"
            [hourStep]="hourStep"
            [minuteStep]="minuteStep"
            [secondStep]="secondStep"
            [disabled]="isDisabled()"
            [ngModel]="value()"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onTimeChange($event)"
          />
        </div>
      </ng-template>
    </div>
  } @else {
    <!-- Time-only: no calendar. -->
    @if (inline) {
      <sui-time-picker
        [seconds]="seconds"
        [meridian]="meridian"
        [hourStep]="hourStep"
        [minuteStep]="minuteStep"
        [secondStep]="secondStep"
        [disabled]="isDisabled()"
        [ngModel]="value()"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onTimeChange($event)"
      />
    } @else {
      <div
        class="sui-date-time-picker__field sui-date-time-picker__field--time"
        #timeFieldEl
      >
        <sui-input
          class="sui-date-time-picker__field-input"
          [id]="effectiveId"
          [placeholder]="placeholder"
          [disabled]="isDisabled()"
          [ngModel]="inputText()"
          [ngModelOptions]="{ standalone: true }"
          [invalid]="inputInvalid()"
          (ngModelChange)="onInputText($event)"
          (keydown.enter)="onInputEnter()"
          (focusin)="onInputFocus()"
          (focusout)="onInputBlur()"
        >
          <button
            sui-suffix
            type="button"
            class="sui-date-time-picker__trigger"
            aria-label="Open time picker"
            [suiPopoverTrigger]="timeTpl"
            [suiPopoverAnchor]="timeFieldEl"
            [suiPopoverContainer]="resolvedPopoverContainer"
            [suiPopoverTriggerDisabled]="isDisabled()"
          >
            <fa-icon [icon]="triggerIcon()" />
          </button>
        </sui-input>
      </div>

      <ng-template #timeTpl>
        <div
          suiPopover
          class="sui-date-time-picker__panel sui-date-time-picker__panel--time"
        >
          <sui-time-picker
            [seconds]="seconds"
            [meridian]="meridian"
            [hourStep]="hourStep"
            [minuteStep]="minuteStep"
            [secondStep]="secondStep"
            [disabled]="isDisabled()"
            [ngModel]="value()"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onTimeChange($event)"
          />
        </div>
      </ng-template>
    }
  }
</div>
